
/**
 * Subtle Loading States - Less visually jarring loading components
 * These components provide smooth transitions without the harsh white backgrounds
 */

/**
 * Skeleton Card Component - Mimics the structure of typical content cards
 */
export const SkeletonCard = ({ 
  title = true, 
  description = true, 
  actions = false,
  className = '' 
}) => {
  return (
    <div className={`bg-white/80 backdrop-blur-sm rounded-lg border border-gray-200/50 p-6 animate-pulse ${className}`}>
      {title && (
        <div className="h-5 bg-gray-200/60 rounded mb-3 w-3/4"></div>
      )}
      {description && (
        <div className="space-y-2 mb-4">
          <div className="h-4 bg-gray-200/60 rounded w-full"></div>
          <div className="h-4 bg-gray-200/60 rounded w-5/6"></div>
          <div className="h-4 bg-gray-200/60 rounded w-4/6"></div>
        </div>
      )}
      {actions && (
        <div className="flex space-x-2">
          <div className="h-8 bg-gray-200/60 rounded w-20"></div>
          <div className="h-8 bg-gray-200/60 rounded w-16"></div>
        </div>
      )}
    </div>
  )
}

/**
 * Skeleton Table Component - Mimics table structure
 */
export const SkeletonTable = ({ 
  rows = 5, 
  columns = 4,
  className = '' 
}) => {
  return (
    <div className={`bg-white/80 backdrop-blur-sm rounded-lg border border-gray-200/50 overflow-hidden ${className}`}>
      {/* Table Header */}
      <div className="bg-gray-50/80 px-6 py-3 border-b border-gray-200/50">
        <div className="flex space-x-4">
          {Array.from({ length: columns }).map((_, index) => (
            <div key={index} className="h-4 bg-gray-200/60 rounded w-24 animate-pulse"></div>
          ))}
        </div>
      </div>
      
      {/* Table Rows */}
      <div className="divide-y divide-gray-200/50">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="px-6 py-4">
            <div className="flex space-x-4">
              {Array.from({ length: columns }).map((_, colIndex) => (
                <div key={colIndex} className="h-4 bg-gray-200/60 rounded animate-pulse" style={{ width: `${Math.random() * 40 + 60}px` }}></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Skeleton List Component - Mimics list structure
 */
export const SkeletonList = ({ 
  items = 6,
  showAvatars = false,
  className = '' 
}) => {
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="bg-white/80 backdrop-blur-sm rounded-lg border border-gray-200/50 p-4 animate-pulse">
          <div className="flex items-center space-x-3">
            {showAvatars && (
              <div className="w-10 h-10 bg-gray-200/60 rounded-full"></div>
            )}
            <div className="flex-1">
              <div className="h-4 bg-gray-200/60 rounded mb-2 w-3/4"></div>
              <div className="h-3 bg-gray-200/60 rounded w-1/2"></div>
            </div>
            <div className="h-6 bg-gray-200/60 rounded w-16"></div>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Subtle Loading Overlay - Minimal loading indicator
 */
export const SubtleLoadingOverlay = ({ 
  isVisible = false, 
  message = 'Loading...',
  className = '' 
}) => {
  if (!isVisible) return null

  return (
    <div className={`fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-40 ${className}`}>
      <div className="bg-white/90 backdrop-blur-md rounded-xl p-6 shadow-lg border border-white/20">
        <div className="flex items-center space-x-3">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-secondary font-medium">{message}</span>
        </div>
      </div>
    </div>
  )
}

/**
 * Route Loading Component - Subtle loading for route transitions
 */
export const RouteLoadingComponent = ({ 
  routeName = 'Content',
  className = '' 
}) => {
  return (
    <div className={`min-h-screen bg-gradient-to-br from-white via-mint-50 to-primary-50 ${className}`}>
      <div className="p-6">
        {/* Header skeleton */}
        <div className="mb-6">
          <div className="h-8 bg-gray-200/60 rounded w-48 mb-2 animate-pulse"></div>
          <div className="h-4 bg-gray-200/60 rounded w-32 animate-pulse"></div>
        </div>

        {/* Content skeleton based on route type */}
        {routeName.toLowerCase().includes('dashboard') && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        )}

        {routeName.toLowerCase().includes('client') && (
          <SkeletonList items={8} showAvatars={true} />
        )}

        {routeName.toLowerCase().includes('invoice') && (
          <SkeletonTable rows={8} columns={5} />
        )}

        {routeName.toLowerCase().includes('job') && (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <SkeletonCard key={index} actions={true} />
            ))}
          </div>
        )}

        {/* Default skeleton for other routes */}
        {!['dashboard', 'client', 'invoice', 'job'].some(route => 
          routeName.toLowerCase().includes(route)
        ) && (
          <div className="space-y-6">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Inline Loading Indicator - For small loading states
 */
export const InlineLoadingIndicator = ({ 
  size = 'sm',
  className = '' 
}) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  }

  return (
    <div className={`inline-flex items-center justify-center ${className}`}>
      <div className={`${sizeClasses[size]} border-2 border-blue-500 border-t-transparent rounded-full animate-spin`}></div>
    </div>
  )
}

/**
 * Button Loading State - For buttons with loading states
 */
export const ButtonLoadingState = ({ 
  children, 
  isLoading = false, 
  loadingText = 'Loading...',
  className = '' 
}) => {
  return (
    <button 
      className={`relative ${className}`}
      disabled={isLoading}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded">
          <InlineLoadingIndicator size="sm" />
          <span className="ml-2 text-sm">{loadingText}</span>
        </div>
      )}
      <span className={isLoading ? 'opacity-0' : 'opacity-100'}>
        {children}
      </span>
    </button>
  )
}